/**
 * Default comparator to sort array data
 * @param  {string} a               a value
 * @param  {string} b               b value
 * @return {number}   1 if a < b, -1 if b > a, otherwise 0
 */
const localeComparator = (a: string, b: string): number => {
  return a.localeCompare(b);
};

/**
 * Returns comparator with the calculated order of the sort based on dir parameter
 * @param  {string} dir                   asc | desc
 * @param  {string} orderBy               field name
 * @return {function}         comparator
 */
export const sortingComparator = (
  dir: string,
  orderBy: string,
): ((a: Record<string, unknown>, b: Record<string, unknown>) => number) => {
  const order = dir === "desc" ? -1 : 1;

  return (a: Record<string, unknown>, b: Record<string, unknown>) =>
    order * localeComparator(String(a[orderBy]), String(b[orderBy]));
};

/**
 * Find an index in an array where a new value can be inserted preserving the order of
 * the items in the array
 * Pre: array is in ascending/descending order
 * Complexity: O(log N)
 * @param  {Array} dataset               a sorted array
 * @param  {string} value                a value to evaluate
 * @param  {Object} sorting              sorting spec as { dir, field }
 *                                       each item of the array is an Object
 * @return {number}         an existing index of the array, otherwise the leftmost index
 */
export const binarySearchInsertionIndex = <
  T extends Record<string, string | number>,
>(
  dataset: T[],
  value: string | number,
  { dir, field }: { dir: string; field: string },
): number => {
  let left = 0;
  let right = dataset.length - 1;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);

    if (dataset[mid][field] === value) {
      break;
    }

    if (dir === "asc") {
      if (dataset[mid][field] < value) {
        left = mid + 1;
      } else if (dataset[mid][field] > value) {
        right = mid - 1;
      }
    } else {
      if (dataset[mid][field] < value) {
        right = mid - 1;
      } else if (dataset[mid][field] > value) {
        left = mid + 1;
      }
    }
  }

  return left;
};
